import {
  Injectable,
  ConflictException,
  UnauthorizedException,
  NotFoundException,
  BadRequestException,
} from '@nestjs/common';
import { InjectRepository } from '@mikro-orm/nestjs';
import { EntityRepository, EntityManager } from '@mikro-orm/mysql';
import { JwtService } from '@nestjs/jwt';
import { ConfigService } from '@nestjs/config';
import * as argon2 from 'argon2';
import { v4 as uuidv4 } from 'uuid';
import { User, UserStatus } from '../entities/user.entity';
import { SignupDto } from '../dto/signup.dto';
import { LoginDto } from '../dto/login.dto';
import { SocialLoginDto } from '../dto/social-login.dto';
import { ChangePasswordDto } from '../dto/change-password.dto';
import { UpdateUserDto } from '../dto/update-user.dto';
import { UserResponseDto } from '../dto/user-response.dto';
import { ApiSuccessResponse } from '@/common/types/api-response';
import { UtilsService } from '@/shared/utils/utils.service';
import { Program } from '../entities/program.entity';
import { ProgramType } from '../entities/program-type.entity';
import { Category } from '../entities/category.entity';
import { UserProgram } from '../entities/user-program.entity';
import { UserCategory } from '../entities/user-category.entity';

@Injectable()
export class UserService {
  constructor(
    @InjectRepository(User)
    private readonly userRepository: EntityRepository<User>,
    @InjectRepository(Program)
    private readonly programRepository: EntityRepository<Program>,
    @InjectRepository(ProgramType)
    private readonly programTypeRepository: EntityRepository<ProgramType>,
    @InjectRepository(Category)
    private readonly categoryRepository: EntityRepository<Category>,
    @InjectRepository(UserProgram)
    private readonly userProgramRepository: EntityRepository<UserProgram>,
    @InjectRepository(UserCategory)
    private readonly userCategoryRepository: EntityRepository<UserCategory>,
    private readonly em: EntityManager,
    private readonly jwtService: JwtService,
    private readonly configService: ConfigService,
    private readonly utilsService: UtilsService,
  ) {}

  async signup(
    signupDto: SignupDto,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    const { name, email, phone, password, image_url, programs, category_ids } =
      signupDto;

    // Check if user already exists
    const existingUser = await this.userRepository.findOne({ email });
    if (existingUser) {
      throw new ConflictException('User with this email already exists');
    }

    // Check if user with same phone exists
    if (phone) {
      const existingUserByPhone = await this.userRepository.findOne({ phone });
      if (existingUserByPhone) {
        throw new ConflictException(
          'User with this phone number already exists',
        );
      }
    }

    // Hash password
    const hashedPassword = await argon2.hash(password);

    // Generate email verification token
    const emailVerificationToken = uuidv4();

    // Create user
    const user = new User();
    user.name = name;
    user.email = email;
    user.phone = phone;
    user.password = hashedPassword;
    if (image_url?.buffer) {
      const file = await this.utilsService.uploadFile(image_url);
      user.image_url = file;
    }
    user.email_verification_token = emailVerificationToken;
    user.status = UserStatus.PENDING_VERIFICATION;
    this.em.persist(user);

    // Handle program associations
    if (programs && programs.length > 0) {
      // ensure a user can belong to only one type per program (no duplicate program ids)
      const seenPrograms = new Set<number>();
      for (const p of programs) {
        if (seenPrograms.has(p.program_id)) {
          throw new BadRequestException(
            `Program ${p.program_id} selected multiple times. A user can belong to only one type of a program.`,
          );
        }
        seenPrograms.add(p.program_id);

        const program = await this.programRepository.findOne(
          { id: p.program_id },
          { populate: ['types'] },
        );
        if (!program) {
          throw new BadRequestException(`Program not found: ${p.program_id}`);
        }

        const programType = await this.programTypeRepository.findOne({
          id: p.type_id,
        });
        if (!programType) {
          throw new BadRequestException(`Program type not found: ${p.type_id}`);
        }

        // Validate that the selected type belongs to the selected program
        const belongsToProgram = program.types
          .getItems()
          .some((t) => t.id === programType.id && t.deleted_at == null);
        if (!belongsToProgram) {
          throw new BadRequestException(
            `Type ${p.type_id} does not belong to Program ${p.program_id}`,
          );
        }

        const userProgram = new UserProgram();
        userProgram.user = user;
        userProgram.program = program;
        userProgram.programType = programType;
        this.em.persist(userProgram);
      }
    }

    // Handle category associations
    if (category_ids && category_ids.length > 0) {
      const categories = await this.categoryRepository.find({
        id: { $in: category_ids },
      });
      const foundIds = new Set(categories.map((c) => c.id));
      const missing = category_ids.filter((id) => !foundIds.has(id));
      if (missing.length > 0) {
        throw new BadRequestException(
          `Categories not found: ${missing.join(', ')}`,
        );
      }

      for (const category of categories) {
        const userCategory = new UserCategory();
        userCategory.user = user;
        userCategory.category = category;
        this.em.persist(userCategory);
      }
    }

    await this.em.flush();

    // TODO: Send verification email here
    // await this.emailService.sendVerificationEmail(user.email, emailVerificationToken);

    // Reload with relations preloaded before mapping
    const loadedUser = await this.userRepository.findOne(
      { id: user.id },
      {
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );

    return {
      success: true,
      statusCode: 201,
      message:
        'User created successfully. Please check your email for verification link.',
      data: this.mapToUserResponse(loadedUser!),
    };
  }

  async login(
    loginDto: LoginDto,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    const { email, password } = loginDto;

    // Find user by email
    const user = await this.userRepository.findOne(
      { email },
      {
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );
    if (!user) {
      throw new UnauthorizedException('Invalid credentials');
    }

    // Check if user is soft deleted
    if (user.isSoftDeleted()) {
      throw new UnauthorizedException('Account has been deactivated');
    }

    // Verify password
    if (!user.password || !(await argon2.verify(user.password, password))) {
      throw new UnauthorizedException('Invalid credentials');
    }

    // Check if email is verified
    // if (!user.isEmailVerified()) {
    //   throw new UnauthorizedException(
    //     'Please verify your email before logging in',
    //   );
    // }

    // Update last login
    user.last_login_at = new Date();
    await this.em.persistAndFlush(user);

    // Generate JWT token
    const payload = { sub: user.id, email: user.email };
    const secret = this.configService.get<string>('config.jwt.secret');
    const access_token = this.jwtService.sign(payload, { secret });

    return {
      success: true,
      statusCode: 200,
      message: 'Login successful',
      data: this.mapToUserResponse(user, access_token),
    };
  }

  async socialLogin(
    socialLoginDto: SocialLoginDto,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    const { provider, provider_id, name, email } = socialLoginDto;

    // Try to find existing user by social provider ID
    let user: User | null = await this.userRepository.findOne(
      { social_provider: provider, social_provider_id: provider_id },
      {
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );

    if (!user && email) {
      // Try to find user by email
      user = await this.userRepository.findOne(
        { email },
        {
          populate: [
            'userPrograms.program',
            'userPrograms.programType',
            'userCategories.category',
          ],
        },
      );

      if (user) {
        // Link social account to existing user
        user.social_provider = provider;
        user.social_provider_id = provider_id;
        user.social_data = socialLoginDto;
      }
    }

    if (!user) {
      // Create new user
      if (!email || !name) {
        throw new BadRequestException(
          'Name and email are required for new social users',
        );
      }

      user = new User();
      user.name = name;
      user.email = email;
      user.social_provider = provider;
      user.social_provider_id = provider_id;
      user.social_data = socialLoginDto;
      user.status = UserStatus.ACTIVE;
      user.email_verified_at = new Date(); // Social accounts are considered verified
    }

    // Check if user is soft deleted
    if (user.isSoftDeleted()) {
      throw new UnauthorizedException('Account has been deactivated');
    }

    // Update last login
    user.last_login_at = new Date();
    await this.em.persistAndFlush(user);

    // Generate JWT token
    const payload = { sub: user.id, email: user.email };
    const secret = this.configService.get<string>('config.jwt.secret');
    const access_token = this.jwtService.sign(payload, { secret });

    return {
      success: true,
      statusCode: 200,
      message: 'Login successful',
      data: this.mapToUserResponse(user, access_token),
    };
  }

  async verifyEmail(token: string): Promise<ApiSuccessResponse<null>> {
    const user = await this.userRepository.findOne({
      email_verification_token: token,
    });

    if (!user) {
      throw new NotFoundException('Invalid verification token');
    }

    user.markAsVerified();
    await this.em.persistAndFlush(user);

    return {
      success: true,
      statusCode: 200,
      message: 'Email verified successfully',
      data: null,
    };
  }

  async resendVerificationEmail(
    email: string,
  ): Promise<ApiSuccessResponse<null>> {
    const user = await this.userRepository.findOne({ email });

    if (!user) {
      throw new NotFoundException('User not found');
    }

    if (user.isEmailVerified()) {
      throw new BadRequestException('Email is already verified');
    }

    // Generate new verification token
    user.email_verification_token = uuidv4();
    await this.em.persistAndFlush(user);

    // TODO: Send verification email here
    // await this.emailService.sendVerificationEmail(user.email, user.email_verification_token);

    return {
      success: true,
      statusCode: 200,
      message: 'Verification email sent successfully',
      data: null,
    };
  }

  async forgotPassword(email: string): Promise<ApiSuccessResponse<null>> {
    const user = await this.userRepository.findOne({ email });

    if (!user) {
      // Don't reveal if email exists or not
      return {
        success: true,
        statusCode: 200,
        message: 'If the email exists, a password reset link has been sent',
        data: null,
      };
    }

    // Generate password reset token
    const resetToken = uuidv4();
    user.password_reset_token = resetToken;
    user.password_reset_expires = new Date(Date.now() + 3600000); // 1 hour

    await this.em.persistAndFlush(user);

    // TODO: Send password reset email here
    // await this.emailService.sendPasswordResetEmail(user.email, resetToken);

    return {
      success: true,
      statusCode: 200,
      message: 'If the email exists, a password reset link has been sent',
      data: null,
    };
  }

  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<ApiSuccessResponse<null>> {
    const user = await this.userRepository.findOne({
      password_reset_token: token,
      password_reset_expires: { $gt: new Date() },
    });

    if (!user) {
      throw new BadRequestException('Invalid or expired reset token');
    }

    // Hash new password
    user.password = await argon2.hash(newPassword);
    user.password_reset_token = null;
    user.password_reset_expires = null;

    await this.em.persistAndFlush(user);

    return {
      success: true,
      statusCode: 200,
      message: 'Password reset successfully',
      data: null,
    };
  }

  async changePassword(
    userId: number,
    changePasswordDto: ChangePasswordDto,
  ): Promise<ApiSuccessResponse<null>> {
    const { current_password, new_password } = changePasswordDto;

    const user = await this.userRepository.findOne(
      { id: userId },
      {
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );
    if (!user) {
      throw new NotFoundException('User not found');
    }

    // Verify current password
    if (
      !user.password ||
      !(await argon2.verify(user.password, current_password))
    ) {
      throw new UnauthorizedException('Current password is incorrect');
    }

    // Prevent using the same password
    if (await argon2.verify(user.password, new_password)) {
      throw new BadRequestException(
        'New password must be different from the current password',
      );
    }

    // Hash new password
    user.password = await argon2.hash(new_password);
    await this.em.persistAndFlush(user);

    return {
      success: true,
      statusCode: 200,
      message: 'Password changed successfully',
      data: null,
    };
  }

  async deleteAccount(userId: number): Promise<ApiSuccessResponse<null>> {
    const user = await this.userRepository.findOne({ id: userId });
    if (!user) {
      throw new NotFoundException('User not found');
    }

    user.markAsDeleted();
    await this.em.persistAndFlush(user);

    return {
      success: true,
      statusCode: 200,
      message: 'Account deleted successfully',
      data: null,
    };
  }

  async getUserList(
    userId: number,
    page: number = 1,
    limit: number = 10,
  ): Promise<ApiSuccessResponse<UserResponseDto[]>> {
    const [users, total] = await this.userRepository.findAndCount(
      { id: { $ne: userId }, deleted_at: null },
      {
        limit,
        offset: (page - 1) * limit,
        orderBy: { created_at: 'DESC' },
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );

    return {
      success: true,
      statusCode: 200,
      message: 'User list retrieved successfully',
      data: users.map((user) => this.mapToUserResponse(user)),
      meta: {
        total,
        perPage: limit,
        currentPage: page,
        lastPage: Math.ceil(total / limit),
      },
    };
  }

  async getUserProfile(
    userId: number,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    const user = await this.userRepository.findOne(
      { id: userId },
      {
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );
    if (!user || user.isSoftDeleted()) {
      throw new NotFoundException('User not found');
    }

    return {
      success: true,
      statusCode: 200,
      message: 'User profile retrieved successfully',
      data: this.mapToUserResponse(user),
    };
  }

  async getUserById(id: number): Promise<ApiSuccessResponse<UserResponseDto>> {
    const user = await this.userRepository.findOne(
      { id },
      {
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );
    if (!user || user.isSoftDeleted()) {
      throw new NotFoundException('User not found');
    }

    return {
      success: true,
      statusCode: 200,
      message: 'User profile retrieved successfully',
      data: this.mapToUserResponse(user),
    };
  }

  async updateUser(
    userId: number,
    updateUserDto: UpdateUserDto,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    const user = await this.userRepository.findOne(
      { id: userId },
      {
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );
    if (!user || user.isSoftDeleted()) {
      throw new NotFoundException('User not found');
    }

    // Check if email is being changed and if it already exists
    if (updateUserDto.email && updateUserDto.email !== user.email) {
      const existingUser = await this.userRepository.findOne({
        email: updateUserDto.email,
      });
      if (existingUser) {
        throw new ConflictException('Email already exists');
      }

      // If email is changed, mark as unverified
      user.email_verified_at = null;
      user.status = UserStatus.PENDING_VERIFICATION;
      user.email_verification_token = uuidv4();
    }

    // Update user scalar fields explicitly (avoid assigning multipart/program/category objects)
    if (updateUserDto.name !== undefined) {
      user.name = updateUserDto.name;
    }
    if (updateUserDto.email !== undefined) {
      user.email = updateUserDto.email;
    }
    if (updateUserDto.phone !== undefined) {
      user.phone = updateUserDto.phone;
    }

    // Handle image upload only when a file is provided; do not overwrite with the raw multipart object
    if (updateUserDto.image_url?.buffer) {
      const file = await this.utilsService.uploadFile(updateUserDto.image_url);
      user.image_url = file;
    }
    this.em.persist(user);

    // Handle program associations if provided (replace existing)
    if (updateUserDto.programs !== undefined) {
      // Remove existing user-program relations
      for (const up of user.userPrograms.getItems()) {
        this.em.remove(up);
      }

      if (updateUserDto.programs && updateUserDto.programs.length > 0) {
        const seenPrograms = new Set<number>();
        for (const p of updateUserDto.programs) {
          if (seenPrograms.has(p.program_id)) {
            throw new BadRequestException(
              `Program ${p.program_id} selected multiple times. A user can belong to only one type of a program.`,
            );
          }
          seenPrograms.add(p.program_id);

          const program = await this.programRepository.findOne(
            { id: p.program_id },
            { populate: ['types'] },
          );
          if (!program) {
            throw new BadRequestException(`Program not found: ${p.program_id}`);
          }

          const programType = await this.programTypeRepository.findOne({
            id: p.type_id,
          });
          if (!programType) {
            throw new BadRequestException(
              `Program type not found: ${p.type_id}`,
            );
          }

          const belongsToProgram = program.types
            .getItems()
            .some((t) => t.id === programType.id && t.deleted_at == null);
          if (!belongsToProgram) {
            throw new BadRequestException(
              `Type ${p.type_id} does not belong to Program ${p.program_id}`,
            );
          }

          const userProgram = new UserProgram();
          userProgram.user = user;
          userProgram.program = program;
          userProgram.programType = programType;
          this.em.persist(userProgram);
        }
      }
    }

    // Handle category associations if provided (replace existing)
    if (updateUserDto.category_ids !== undefined) {
      // Remove existing user-category relations
      for (const uc of user.userCategories.getItems()) {
        this.em.remove(uc);
      }

      if (updateUserDto.category_ids && updateUserDto.category_ids.length > 0) {
        const categories = await this.categoryRepository.find({
          id: { $in: updateUserDto.category_ids },
        });
        const foundIds = new Set(categories.map((c) => c.id));
        const missing = updateUserDto.category_ids.filter(
          (id) => !foundIds.has(id),
        );
        if (missing.length > 0) {
          throw new BadRequestException(
            `Categories not found: ${missing.join(', ')}`,
          );
        }

        for (const category of categories) {
          const userCategory = new UserCategory();
          userCategory.user = user;
          userCategory.category = category;
          this.em.persist(userCategory);
        }
      }
    }

    await this.em.flush();

    // Generate JWT token
    const payload = { sub: user.id, email: user.email };
    const secret = this.configService.get<string>('config.jwt.secret');
    const access_token = this.jwtService.sign(payload, { secret });

    // Reload with relations to ensure preloaded before mapping
    const loadedUser = await this.userRepository.findOne(
      { id: user.id },
      {
        populate: [
          'userPrograms.program',
          'userPrograms.programType',
          'userCategories.category',
        ],
      },
    );

    return {
      success: true,
      statusCode: 200,
      message: 'User updated successfully',
      data: this.mapToUserResponse(loadedUser!, access_token),
    };
  }

  private mapToUserResponse(
    user: User,
    access_token?: string,
  ): UserResponseDto {
    const programs = user.userPrograms.getItems().map((up) => ({
      program_id: up.program.id,
      program_name: up.program.name,
      type_id: up.programType.id,
      type_name: up.programType.name,
    }));

    const categories = user.userCategories.getItems().map((uc) => ({
      id: uc.category.id,
      name: uc.category.name,
      slug: uc.category.slug ?? null,
    }));

    return {
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      status: user.status,
      email_verified_at: user.email_verified_at,
      social_provider: user.social_provider,
      last_login_at: user.last_login_at,
      image_url: user.image_url
        ? `${this.configService.get<string>('config.baseUrl')}/${user.image_url}`
        : null,
      created_at: user.created_at,
      updated_at: user.updated_at,
      programs,
      categories,
      access_token,
    };
  }

  async listPrograms(): Promise<
    ApiSuccessResponse<
      Array<{
        id: number;
        name: string;
        slug: string | null | undefined;
        types: Array<{
          id: number;
          name: string;
          slug: string | null | undefined;
        }>;
      }>
    >
  > {
    // Fetch all programs with their types, excluding soft-deleted records
    const programs = await this.programRepository.find(
      { deleted_at: null },
      { populate: ['types'] },
    );

    const data = programs.map((p) => ({
      id: p.id,
      name: p.name,
      slug: p.slug,
      types: p.types
        .getItems()
        .filter((t) => t.deleted_at == null)
        .map((t) => ({ id: t.id, name: t.name, slug: t.slug })),
    }));

    return {
      success: true,
      statusCode: 200,
      message: 'Programs with types retrieved successfully',
      data,
    };
  }

  async listCategories(): Promise<
    ApiSuccessResponse<
      Array<{
        id: number;
        name: string;
        slug: string | null | undefined;
      }>
    >
  > {
    const categories = await this.categoryRepository.find(
      { deleted_at: null },
      { orderBy: { name: 'ASC' } },
    );

    const data = categories.map((c) => ({
      id: c.id,
      name: c.name,
      slug: c.slug,
    }));

    return {
      success: true,
      statusCode: 200,
      message: 'Categories retrieved successfully',
      data,
    };
  }
}
